import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, BookOpen } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { getStoryBySlug, stories } from "@/data/stories";
import { characters as allChars } from "@/data/characters";

export default function StoryPage() {
  const { slug } = useParams();
  const story = getStoryBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!story) {
    return (
      <div className="min-h-[70vh] pt-40 max-w-3xl mx-auto px-6 text-center" data-testid="story-not-found">
        <h1 className="font-display text-4xl md:text-5xl font-semibold text-[#051937]">
          Ta bajka jeszcze się nie napisała ✨
        </h1>
        <p className="mt-6 text-lg text-[#0b2a55]/75">
          Wróć na stronę główną i wybierz inną historię.
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 mt-10 rounded-full bg-[#051937] text-white px-6 py-3 font-semibold"
          data-testid="not-found-back"
        >
          <ArrowLeft size={16} /> Do bajkowego świata
        </Link>
      </div>
    );
  }

  const cast = story.cast
    .map((s) => allChars.find((c) => c.slug === s))
    .filter(Boolean);

  const currentIdx = stories.findIndex((s) => s.slug === slug);
  const prev = stories[(currentIdx - 1 + stories.length) % stories.length];
  const next = stories[(currentIdx + 1) % stories.length];

  return (
    <article
      className="relative pb-24"
      style={{ background: `linear-gradient(180deg, ${story.color}22 0%, #F4FAFF 60%)` }}
      data-testid="story-page"
    >
      {/* Hero of the story */}
      <div className="relative pt-32 md:pt-40 pb-16 md:pb-20 max-w-6xl mx-auto px-6 md:px-10">
        <Link
          to="/#bajki"
          className="inline-flex items-center gap-2 chip !bg-white/80 !border-white hover:bg-white"
          data-testid="story-back"
        >
          <ArrowLeft size={14} /> Wróć do bajek
        </Link>

        <div className="mt-10 grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-8">
            <span
              className="chapter-number block text-[7rem] md:text-[10rem] leading-none"
              style={{ color: story.accent }}
              data-testid="story-number"
            >
              {story.number}
            </span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-semibold text-[#051937] text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight mt-4"
              data-testid="story-title"
            >
              {story.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.15 }}
              className="mt-6 text-xl text-[#0b2a55]/80 max-w-2xl font-script"
            >
              {story.subtitle}
            </motion.p>
          </div>

          <div className="col-span-12 md:col-span-4 flex md:justify-end">
            <div className="chip !bg-white text-[#051937] flex-col !items-start !py-3 !px-5 !rounded-3xl gap-1">
              <span className="flex items-center gap-2">
                <BookOpen size={14} /> {story.duration}
              </span>
              <span className="normal-case tracking-normal text-[#0b2a55]/60 text-xs font-medium">
                Rozdział {story.number}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Scene image */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        className="relative max-w-6xl mx-auto px-6 md:px-10"
      >
        <div className="spotlight h-[280px] sm:h-[380px] md:h-[520px]">
          <img
            src={story.scene}
            alt={story.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      </motion.div>

      {/* Cast row */}
      <div className="max-w-6xl mx-auto px-6 md:px-10 mt-16">
        <span className="chip"><span className="dot" /> W tej bajce grają</span>
        <ul className="mt-6 flex flex-wrap gap-4 md:gap-6" data-testid="story-cast">
          {cast.map((c) => (
            <li key={c.slug}>
              <div
                className="flex items-center gap-3 rounded-full bg-white border border-[#BAE6FD]/70 pl-2 pr-5 py-2"
                data-testid={`cast-${c.slug}`}
              >
                <span
                  className="w-11 h-11 rounded-full overflow-hidden grid place-items-center"
                  style={{ background: `${c.color}66` }}
                >
                  <img src={c.portrait} alt={c.name} className="w-11 h-11 object-contain" />
                </span>
                <div>
                  <div className="font-display font-semibold text-[#051937] text-base leading-tight">
                    {c.name}
                  </div>
                  <div className="text-xs uppercase tracking-[0.16em] text-[#0b2a55]/60 font-bold">
                    {c.role}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Body */}
      <div className="max-w-3xl mx-auto px-6 md:px-10 mt-20">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="font-display text-2xl md:text-3xl leading-snug text-[#051937] font-medium first-letter:font-display first-letter:font-semibold first-letter:text-[5rem] first-letter:float-left first-letter:mr-3 first-letter:leading-[0.85] first-letter:mt-1"
          data-testid="story-intro"
          style={{ color: "#051937" }}
        >
          {story.intro}
        </motion.p>

        <div className="mt-10 space-y-6">
          {story.paragraphs.map((p, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: i * 0.05 }}
              className="text-lg md:text-xl leading-relaxed text-[#0b2a55]/90"
            >
              {p}
            </motion.p>
          ))}
        </div>

        {/* Moral */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="mt-16 relative rounded-[2rem] p-8 md:p-10"
          style={{ background: `${story.color}66`, border: `1px solid ${story.accent}55` }}
          data-testid="story-moral"
        >
          <span className="chip !bg-white/80 !text-[#051937]">
            <span className="dot" /> Cichy morał
          </span>
          <p className="mt-4 font-script text-2xl md:text-3xl text-[#051937] leading-snug">
            „{story.moral}”
          </p>
        </motion.div>
      </div>

      {/* Prev / next */}
      <div className="max-w-6xl mx-auto px-6 md:px-10 mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <Link
          to={`/bajka/${prev.slug}`}
          className="group rounded-[1.75rem] bg-white border border-[#BAE6FD]/70 p-6 md:p-8 flex items-center justify-between hover:-translate-y-1 transition-transform"
          data-testid="story-prev"
        >
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0b2a55]/60">
              Poprzednia bajka
            </div>
            <div className="mt-2 font-display font-semibold text-xl md:text-2xl text-[#051937]">
              {prev.title}
            </div>
          </div>
          <span className="w-12 h-12 rounded-full grid place-items-center bg-[#F4FAFF] group-hover:bg-[#84C5F4] transition-colors">
            <ArrowLeft />
          </span>
        </Link>
        <Link
          to={`/bajka/${next.slug}`}
          className="group rounded-[1.75rem] bg-white border border-[#BAE6FD]/70 p-6 md:p-8 flex items-center justify-between hover:-translate-y-1 transition-transform"
          data-testid="story-next"
        >
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0b2a55]/60">
              Następna bajka
            </div>
            <div className="mt-2 font-display font-semibold text-xl md:text-2xl text-[#051937]">
              {next.title}
            </div>
          </div>
          <span className="w-12 h-12 rounded-full grid place-items-center bg-[#F4FAFF] group-hover:bg-[#FDE68A] transition-colors">
            <ArrowRight />
          </span>
        </Link>
      </div>

      {/* Back to home */}
      <div className="max-w-6xl mx-auto px-6 md:px-10 mt-16 text-center">
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-full bg-[#051937] text-white px-7 py-4 font-semibold shadow-[0_20px_50px_-20px_rgba(11,42,85,0.6)]"
          data-testid="story-home"
        >
          Wróć do bajkowego świata <ArrowUpRight size={16} />
        </Link>
      </div>
    </article>
  );
}
